import { Injectable } from '@angular/core';
import {
	ActivatedRouteSnapshot,
	RouterStateSnapshot,
	Router,
} from '@angular/router';
import { Company, CompanyResolutionType } from 'company-finder-common';
import { AuthnService, CompanyService, Logger } from '@Common';
import { AssetService, UserService, WebAnalyticsService } from '../services/';

@Injectable({ providedIn: 'root' })
export class CompanyResolver {
	protected logger = new Logger(this.constructor.name);
	protected companyResolutionType: CompanyResolutionType;

	constructor(
		protected companyService: CompanyService,
		protected authService: AuthnService,
		protected userService: UserService,
		protected webAnalyticsService: WebAnalyticsService,
		private router: Router,
		protected assetService: AssetService
	) {}

	async resolve(
		route: ActivatedRouteSnapshot,
		_state: RouterStateSnapshot
	): Promise<Company> {
		try {
			this.companyResolutionType = route.data['companyResolutionType'];
			const company = await this.loadCompany(
				route.params.id,
				route.params.name
			);
			if (company) {
				return company;
			}
		} catch (error) {
			this.logger.warn(`Company resolver failed: ${error}`);
		}
		this.router.navigate(['/']);
	}

	private async loadCompany(id: string, name: string): Promise<Company> {
		if (id) {
			return await this.loadCompanyById(id);
		} else if (name) {
			return await this.loadCompanyByName(name);
		} else if (this.authService.isAuthenticated) {
			return await this.loadCompanyByAssociation();
		}
	}

	private async loadCompanyByName(name: string) {
		return await this.companyService.getByEncodedNameOrOppId(
			name,
			this.companyResolutionType
		);
	}

	private async loadCompanyById(id: string) {
		return await this.companyService.getById(id);
	}

	private async loadCompanyByAssociation(): Promise<Company> {
		// Find the company for a Company Contact hitting /company/update with no company name
		const associatedCompanies = this.userService.companiesForUpdate;

		if (!(associatedCompanies?.length > 0)) {
			return;
		}

		if (associatedCompanies?.length === 1) {
			const company = await this.companyService.getByNameOrOppId(
				associatedCompanies[0],
				this.companyResolutionType
			);

			if (!company) {
				this.webAnalyticsService.trackEvent('error', {
					category: 'juniverse-integration',
					event: `unmatched-company`,
					label: `${this.authService.userId ?? ''}:${
						this.authService.juniverseToken
					}`,
				});
				this.logger.warn(
					`Navigator can not match the company "${associatedCompanies[0]}" to any company in the index`
				);
			} else {
				return company;
			}
		} else {
			this.logger.warn(
				`Navigator can not automatically pick among the JUniverse user's ${associatedCompanies?.length} associated companies`
			);
		}
	}
}
